/** Top-bar theme switcher — System / Light / Dark (default: System, follows the OS).
 *
 * A compact 3-way segmented control matching the language switcher. Line icons (no emoji), each
 * button is a labelled toggle (aria-pressed + title) so it's keyboard- and screen-reader-accessible.
 */
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./useTheme";
import type { ThemeMode } from "./useTheme";

function SystemIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

function LightIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function DarkIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

const OPTIONS: { mode: ThemeMode; labelKey: string; icon: JSX.Element }[] = [
  { mode: "system", labelKey: "theme.system", icon: <SystemIcon /> },
  { mode: "light", labelKey: "theme.light", icon: <LightIcon /> },
  { mode: "dark", labelKey: "theme.dark", icon: <DarkIcon /> },
];

export function ThemeSwitcher() {
  const { t } = useTranslation();
  const { mode, setMode } = useTheme();

  return (
    <div className="lang-switch theme-switch" role="group" aria-label={t("theme.label")}>
      {OPTIONS.map((o) => {
        const label = t(o.labelKey);
        return (
          <button
            key={o.mode}
            type="button"
            className={`lang-switch__btn theme-switch__btn ${
              mode === o.mode ? "lang-switch__btn--active" : ""
            }`.trim()}
            aria-pressed={mode === o.mode}
            aria-label={label}
            title={label}
            onClick={() => setMode(o.mode)}
          >
            {o.icon}
          </button>
        );
      })}
    </div>
  );
}
